import type { FeatureKey } from "@/components/auth/AuthProvider";
import type { ServiceKey, ServiceSettingsRow } from "@/hooks/useSiteSettings";
import type { Palette } from "@/components/theme/PaletteProvider";
import { FREE_TOOLS } from "@/components/freeTools/FreeToolsNav";

const DEFAULT_IMAGE_BY_SERVICE_KEY: Partial<Record<ServiceKey, string>> = {
  paintbooth: "https://comms.space101.store/storage/v1/object/public/site-assets/services/paintbooth/card/1776900255488-ed023e9a22e788.png",
  itemimage: "https://fkcobnobusicmldplods.supabase.co/storage/v1/object/public/site-assets/services/itemimage/card/1777395949631-3bce045e440978.png",
  uvstudio: "https://comms.space101.store/storage/v1/object/public/site-assets/services/uvstudio/card/1776900338483-c2f6947e88043.png",
  eupviewer: "https://comms.space101.store/storage/v1/object/public/site-assets/services/eupviewer/card/1776900349223-51be3d088c0008.png",
  weaponviewer: "https://comms.space101.store/storage/v1/object/public/site-assets/services/weaponviewer/card/1776881149662-05457de076a47.png",
  blinklab: "https://comms.space101.store/storage/v1/object/public/site-assets/services/blinklab/card/1776900559060-6009873edde638.png",
  metaroom: "https://comms.space101.store/storage/v1/object/public/site-assets/services/metaroom/card/1776900615450-b5ac90813e7818.png",
  packbench: "https://comms.space101.store/storage/v1/object/public/site-assets/services/packbench/card/1776900418164-20b4a3241e75e8.png",
  codesmith: "https://comms.space101.store/storage/v1/object/public/site-assets/services/codesmith/card/1776900547164-5fa69e2d11ee7.png",
  tripoai: "https://fkcobnobusicmldplods.supabase.co/storage/v1/object/public/site-assets/services/tripoai/card/1777985508588-c3a6516dd52148.png",
};

/**
 * Bundled previews for the free `/free/**` tools, which have no service row and
 * so no admin-managed card image. Captured as a signed-in subscriber (tool only,
 * no article/FAQ) at 1920x1080 in dark mode, one per palette:
 * `public/free-tool-previews/<key>.png` and `<key>-cobalt.png`. An admin-set
 * image for the same route still wins.
 */
export function freeToolPreviewImage(href: string, palette: Palette): string | null {
  const tool = FREE_TOOLS.find((t) => t.href === href);
  if (!tool) return null;
  return `/free-tool-previews/${tool.key}${palette === "cobalt" ? "-cobalt" : ""}.png`;
}

/**
 * Bundled previews for paid tools that have no admin-managed card image yet,
 * keyed by route: `public/paid-tool-previews/<name>.png` and `<name>-cobalt.png`.
 * Same capture as the free ones (1920x1080, dark, subscriber view), but with
 * real data loaded so the preview shows the tool's output. Uploading a card
 * image in the admin site settings overrides these.
 */
export const PAID_TOOL_PREVIEW_BY_HREF: Record<string, string> = {
  "/handling-diff": "handling-diff",
  "/resmon": "resmon",
  "/assets": "assets",
};

export function paidToolPreviewImage(href: string, palette: Palette): string | null {
  const name = PAID_TOOL_PREVIEW_BY_HREF[href];
  if (!name) return null;
  return `/paid-tool-previews/${name}${palette === "cobalt" ? "-cobalt" : ""}.png`;
}

function normalizePath(value: string | null | undefined) {
  return value?.trim() || null;
}

/**
 * Read the palette the pre-hydration script committed to <html data-palette>.
 * These cards resolve client-side (serviceSettings come from client hooks), so
 * the attribute is set by the time this runs; defaults to "classic" off-DOM.
 */
function activePalette(): Palette {
  if (typeof document !== "undefined") {
    const attr = document.documentElement.dataset.palette;
    if (attr === "cobalt" || attr === "classic") return attr;
  }
  return "classic";
}

/**
 * Pick the palette-appropriate card image for a service row: the cobalt-scheme
 * override when the cobalt palette is active and one is set, otherwise the
 * default (classic) card image. Shared with LandingToolsGrid so both surfaces
 * agree on the rule.
 */
export function pickPaletteCardImage(
  row: Pick<ServiceSettingsRow, "card_image_url" | "card_image_url_cobalt"> | null | undefined,
  palette: Palette,
): string | null {
  if (!row) return null;
  if (palette === "cobalt") {
    const cobalt = normalizePath(row.card_image_url_cobalt);
    if (cobalt) return cobalt;
  }
  return normalizePath(row.card_image_url);
}

// --- DEV-ONLY: preview the recolored "Cobalt" tool screenshots locally ---
// Files live in public/tool-previews/<key>.png (gitignored, not deployed). This
// override only fires when NODE_ENV !== "production", so production keeps serving
// the live Supabase card_image_url images untouched. Remove this block (and the
// public/tool-previews folder) to drop the local preview.
const LOCAL_PREVIEW_KEYS = new Set<string>([
  "addoneupcreator", "badgemaker", "blinklab", "chevronbuilder", "codesmith",
  "codevisualizer", "creatorconvert", "embroiderytool", "eupviewer", "itemimage",
  "metaroom", "packbench", "paintbooth", "paintboothstudio", "scriptdocs",
  "tripoai", "uvstudio", "weaponviewer",
]);

function devLocalPreview(
  href: string,
  featureKey: FeatureKey | undefined,
  serviceSettings: ServiceSettingsRow[],
  palette: Palette,
): string | null {
  if (process.env.NODE_ENV === "production") return null;
  // The bundled files are cobalt recolors — only stand in for the blue scheme,
  // so classic users still see the live (classic) card images even in dev.
  if (palette !== "cobalt") return null;
  // The palette is read off the DOM, which the server can't see, so resolving
  // here before the client-only settings query lands makes SSR and the first
  // client render disagree (hydration mismatch). Wait for the settings.
  if (serviceSettings.length === 0) return null;
  let key: string | null = null;
  const nh = normalizePath(href);
  if (nh) {
    const row = serviceSettings.find((r) => normalizePath(r.href) === nh);
    if (row) key = row.key;
  }
  if (!key && featureKey) key = featureKey;
  if (key && LOCAL_PREVIEW_KEYS.has(key)) return `/tool-previews/${key}.png`;
  return null;
}

export function resolveToolPreviewImage({
  href,
  featureKey,
  serviceSettings,
}: {
  href: string;
  featureKey?: FeatureKey;
  serviceSettings: ServiceSettingsRow[];
}) {
  const palette = activePalette();
  const localPreview = devLocalPreview(href, featureKey, serviceSettings, palette);
  if (localPreview) return localPreview;

  const normalizedHref = normalizePath(href);

  if (normalizedHref) {
    const routeMatch = serviceSettings.find((row) => normalizePath(row.href) === normalizedHref);
    const routeImage = pickPaletteCardImage(routeMatch, palette);
    if (routeImage) return routeImage;

    const defaultRouteMatch = serviceSettings.find(
      (row) => normalizePath(row.href) === normalizedHref && !!DEFAULT_IMAGE_BY_SERVICE_KEY[row.key],
    );
    if (defaultRouteMatch) {
      return DEFAULT_IMAGE_BY_SERVICE_KEY[defaultRouteMatch.key] ?? null;
    }
  }

  if (!featureKey) return normalizedHref ? freeToolPreviewImage(normalizedHref, palette) : null;

  const featureMatch = serviceSettings.find((row) => row.key === featureKey);
  const featureImage = pickPaletteCardImage(featureMatch, palette);
  if (featureImage) return featureImage;

  return DEFAULT_IMAGE_BY_SERVICE_KEY[featureKey] ?? (normalizedHref ? paidToolPreviewImage(normalizedHref, palette) : null);
}
